import React, { useEffect, useMemo, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFormikContext, Field, getIn } from "formik";
import PropTypes from "prop-types";
import get from "lodash/get";
import isEqual from "lodash/isEqual";

import { updateProspect } from "../../../store/actions/appConfig";
import { getDatalist } from "../../../store/selectors/appConfig";

export const AutoSaveField = ({
  name,
  path,
  isLoadDefaultValueFromStore = true,
  datalistId,
  datalistSearch,
  codeType = "",
  filterOptionsDeps,
  filterOptions = option => option,
  changeProspect = prospect => prospect,
  initialValue = "",
  defaultValue = "",
  ...rest
}) => {
  const dispatch = useDispatch();
  const appConfig = useSelector(state => state.appConfig);
  const datalist = useSelector(getDatalist);
  const {
    values,
    touched,
    setFieldError,
    setFieldValue,
    validateForm,
    setFieldTouched
  } = useFormikContext();

  const value = getIn(values, name);
  const touch = getIn(touched, name);
  const serverValidationError = null;
  const [isLoadedDefaultValueFromStore, setIsLoadedDefaultValueFromStore] = useState(
    !isLoadDefaultValueFromStore
  );
  const timer = useRef(null);

  useEffect(() => {
    if (!isLoadedDefaultValueFromStore && path) {
      const value = get(appConfig, path);

      if (value !== undefined) {
        setFieldValue(name, value);
      }
      setIsLoadedDefaultValueFromStore(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (serverValidationError) {
      setFieldError(name, serverValidationError.message);
    }
  }, [setFieldError, serverValidationError, name]);

  useEffect(() => {
    if (isLoadedDefaultValueFromStore && path && appConfig) {
      const newTimer = setTimeout(() => {
        const oldValue = get(appConfig, path, initialValue);

        if (!isEqual(oldValue, value)) {
          validateForm().then(errors => {
            if (!getIn(errors, name)) {
              const prospect = changeProspect({ [path]: value }, value, path, errors);
              dispatch(updateProspect(prospect));
            } else if (!touch) {
              setFieldTouched(name);
            }
          });
        } else {
          setFieldError(name);
        }
      }, 500);

      if (timer.current) clearTimeout(timer.current);
      timer.current = newTimer;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadedDefaultValueFromStore, value]);

  const options = useMemo(() => {
    if (path && datalistId && datalist) {
      const fieldConfig = datalist[datalistId] || [];
      return filterOptions(fieldConfig);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, datalistId, filterOptions, filterOptionsDeps]);
  // Create a custom prop for the Field component that won't be passed to DOM
  return (
    <Field
      name={name}
      options={options}
      defaultValue={defaultValue}
      codeType={codeType}
      {...rest}
    />
  );
};

AutoSaveField.propTypes = {
  name: PropTypes.string.isRequired,
  path: PropTypes.string,
  isLoadDefaultValueFromStore: PropTypes.bool,
  datalistId: PropTypes.string,
  datalistSearch: PropTypes.string,
  codeType: PropTypes.string,
  filterOptionsDeps: PropTypes.array,
  filterOptions: PropTypes.func,
  changeProspect: PropTypes.func,
  initialValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
    PropTypes.object,
    PropTypes.array
  ]),
  defaultValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
    PropTypes.object,
    PropTypes.array
  ])
};
